import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { taskTodayYMD } from '../../services/tasks.service';

/** Quick picks (Today, Tomorrow, 2 days, 7 days) plus a date field, in a small popover. */
@Component({
  selector: 'tk-due-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <span class="tk-dp-trigger" (click)="toggle($event)"><ng-content></ng-content></span>
    @if (open) {
      <div class="tk-dp" [class.right]="align === 'right'" (click)="$event.stopPropagation()">
        <div class="tk-dp-quick">
          @for (q of quick; track q.label) {
            <button type="button" [class.on]="value === q.ymd" (click)="pick(q.ymd)">{{ q.label }}<small>{{ q.sub }}</small></button>
          }
        </div>
        <label class="tk-dp-date">
          <span>Pick a date</span>
          <input type="date" [ngModel]="value || ''" (ngModelChange)="pick($event || null)" [min]="min" />
        </label>
        @if (withTime) {
          <div class="tk-dp-time">
            <span>Time (optional)</span>
            <small class="why">A reminder goes out at that time</small>
            <div class="row">
              <input type="time" [ngModel]="time || ''" (ngModelChange)="pickTime($event || null)" [disabled]="!value" />
              @for (q of quickTimes; track q.hhmm) {
                <button type="button" [class.on]="time === q.hhmm" [disabled]="!value" (click)="pickTime(q.hhmm)">{{ q.label }}</button>
              }
              @if (time) {
                <button type="button" class="clr" (click)="pickTime(null)" title="No time">×</button>
              }
            </div>
          </div>
        }
        @if (allowNone) {
          <button type="button" class="tk-dp-none" (click)="pick(null)">No due date</button>
        }
      </div>
    }
  `,
  styles: [
    `
      :host { position: relative; display: inline-block; }
      .tk-dp-trigger { display: inline-flex; cursor: pointer; }
      .tk-dp { position: absolute; z-index: 60; top: calc(100% + 6px); left: 0; width: 296px; background: var(--tk-card); border: 1px solid var(--tk-line); border-radius: 12px; box-shadow: var(--tk-shadow-lg); padding: 8px; }
      .tk-dp.right { left: auto; right: 0; }
      .tk-dp-quick { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
      .tk-dp-quick button { font-family: inherit; display: flex; flex-direction: column; align-items: flex-start; gap: 1px; padding: 8px 10px; border: 1px solid var(--tk-line); border-radius: 8px; background: var(--tk-card); color: var(--tk-ink); font-size: 13px; font-weight: 600 !important; cursor: pointer; line-height: 1.2; }
      .tk-dp-quick button small { font-size: 11px; color: var(--tk-ink-3); font-weight: 500 !important; }
      .tk-dp-quick button:hover { background: var(--tk-hover); }
      .tk-dp-quick button.on { border-color: var(--tk-brand); background: var(--tk-brand-soft); color: var(--tk-brand-ink); }
      .tk-dp-date { display: flex; flex-direction: column; gap: 4px; margin: 8px 2px 2px; font-size: 11px; color: var(--tk-ink-3); text-transform: uppercase; letter-spacing: 0.06em; }
      .tk-dp-date input { font-family: inherit; width: 100%; padding: 7px 10px; border: 1px solid var(--tk-line); border-radius: 8px; background: var(--tk-input); color: var(--tk-ink); font-size: 13px; }
      .tk-dp-time { display: flex; flex-direction: column; gap: 5px; margin: 10px 2px 2px; font-size: 11px; color: var(--tk-ink-3); text-transform: uppercase; letter-spacing: 0.06em; }
      .tk-dp-time .row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; margin-top: 2px; }
      .tk-dp-time small.why { text-transform: none; letter-spacing: 0; font-size: 11.5px; color: var(--tk-ink-3); margin-top: -3px; }
      .tk-dp-time input { font-family: inherit; flex: 1 1 100%; width: 100%; min-width: 0; padding: 6px 8px; border: 1px solid var(--tk-line); border-radius: 8px; background: var(--tk-input); color: var(--tk-ink); font-size: 13px; }
      .tk-dp-time button { font-family: inherit; height: 28px; padding: 0 9px; border: 1px solid var(--tk-line); border-radius: 999px; background: var(--tk-card); color: var(--tk-ink-2); font-size: 12px; cursor: pointer; text-transform: none; letter-spacing: 0; }
      .tk-dp-time button:hover { background: var(--tk-hover); }
      .tk-dp-time button.on { border-color: var(--tk-brand); background: var(--tk-brand-soft); color: var(--tk-brand-ink); }
      .tk-dp-time button.clr { width: 28px; padding: 0; font-size: 15px; }
      .tk-dp-time button:disabled, .tk-dp-time input:disabled { opacity: 0.5; cursor: default; }
      .tk-dp-none { font-family: inherit; width: 100%; margin-top: 6px; padding: 7px; border: 0; background: transparent; color: var(--tk-ink-3); font-size: 12.5px; border-radius: 8px; cursor: pointer; }
      .tk-dp-none:hover { background: var(--tk-hover); color: var(--tk-ink); }
    `,
  ],
})
export class DuePickerComponent {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  @Input() value: string | null = null;
  @Input() allowNone = true;
  @Input() align: 'left' | 'right' = 'left';
  /** Show the optional time of day (HH:mm) row. */
  @Input() withTime = false;
  @Input() time: string | null = null;
  @Output() valueChange = new EventEmitter<string | null>();
  @Output() timeChange = new EventEmitter<string | null>();
  readonly quickTimes = [
    { label: '10 am', hhmm: '10:00' },
    { label: '2 pm', hhmm: '14:00' },
    { label: '5 pm', hhmm: '17:00' },
  ];
  open = false;
  min = '2020-01-01';

  get quick() {
    const day = (n: number) => {
      const d = new Date();
      d.setDate(d.getDate() + n);
      return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    };
    return [
      { label: 'Today', sub: day(0), ymd: taskTodayYMD(0) },
      { label: 'Tomorrow', sub: day(1), ymd: taskTodayYMD(1) },
      { label: 'In 2 days', sub: day(2), ymd: taskTodayYMD(2) },
      { label: 'In a week', sub: day(7), ymd: taskTodayYMD(7) },
    ];
  }

  toggle(ev?: Event) {
    ev?.stopPropagation();
    this.open = !this.open;
    this.cdr.markForCheck();
  }
  pick(v: string | null) {
    this.value = v;
    this.valueChange.emit(v);
    if (!v && this.time) this.pickTime(null);
    // With a time row the popover stays open so a time can follow the date.
    this.open = this.withTime && !!v && !this.time ? true : false;
    this.cdr.markForCheck();
  }
  pickTime(v: string | null) {
    const t = v && /^\d{2}:\d{2}$/.test(v) ? v : null;
    if (t === this.time) return;
    this.time = t;
    this.timeChange.emit(t);
    this.cdr.markForCheck();
  }
  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    if (this.open && !this.host.nativeElement.contains(ev.target as Node)) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }
  @HostListener('document:keydown.escape')
  onEsc() {
    if (this.open) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }
}
